import React from 'react';
import { Link } from 'react-router-dom';
import Mac from '../assets/images/MacBookpro.png';
// Import the CSS module
import styles from '../HomePage.module.css';
// Import selected icons from Heroicons (outline style)
import {
  ArrowDownTrayIcon,
  SparklesIcon,
  LockClosedIcon,
  DevicePhoneMobileIcon,
  SquaresPlusIcon,
  UserIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';

function HomePage() {
  return (
    // Use a subtle gradient or background image for the whole page if possible
    // to make backdrop-blur effective. Here, a light gray fallback.
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 font-sans text-gray-800 overflow-x-hidden">

      {/* --- Hero Section --- */}
      <section className="relative pt-32 pb-24 md:pt-48 md:pb-32 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-50">
          {/* Optional: Add a subtle background pattern or large blurred shapes */}
        </div>
        <div className="container mx-auto px-6 relative z-10">
          {/* Icon Placeholder: Maybe a subtle app logo or abstract shape */}
          {/* <CubeTransparentIcon className="h-16 w-16 mx-auto mb-6 text-blue-500 opacity-80" /> */}
          <div className="inline-block bg-blue-100/50 text-blue-700 text-sm font-medium px-4 py-1 rounded-full mb-4 backdrop-blur-sm border border-blue-200/50">
            Meet Your Smart Shopping Companion
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight leading-tight">
            Shop Smarter, <br className="hidden sm:block" /> Not Harder.
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Unify your carts, organize effortlessly, and checkout seamlessly across the web.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-16">
            <Link
              to="/register"
              className="bg-blue-600 text-white font-semibold py-3 px-8 rounded-full shadow-lg hover:shadow-blue-500/40 hover:bg-blue-700 transition duration-300 ease-in-out transform hover:-translate-y-0.5"
            >
              Get Started Free
            </Link>
            <Link
              to="/download" // Assuming you have a download page route
              className="flex items-center gap-2 bg-white/70 text-gray-800 font-medium py-3 px-6 rounded-full shadow-md backdrop-blur-md border border-gray-200/50 hover:bg-white hover:shadow-lg transition duration-300 ease-in-out"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Download Extension</span>
            </Link>
          </div>
          {/* Floating Image Effect */}
          <div className={`relative mt-12 ${styles.heroImageContainer}`}>
             {/* Apply custom glow class */}
             <div className={styles.heroBackgroundGlow}></div>
             <img
               src={Mac}
                // Apply custom image class alongside Tailwind classes
               className={`w-full max-w-xl lg:max-w-2xl relative z-10 shadow-2xl rounded-2xl ${styles.heroImage}`}
               alt="Shopping Assistant Interface"
             />
          </div>
        </div>
      </section>

      {/* --- Features Section --- */}
      <section className="py-24 bg-white/50 backdrop-blur-lg border-t border-b border-gray-200/50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">Why You'll Love ShopEase</h2>
          <p className="text-center text-gray-600 mb-16 max-w-xl mx-auto">
             Go beyond simple wishlists. Manage your entire online shopping workflow from one place.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature Card 1 */}
            <div className={`p-8 rounded-3xl transition duration-300 ${styles.glassCard}`}>
              <SparklesIcon className="h-10 w-10 text-purple-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Seamless Integration</h3>
              <p className="text-gray-600">Add items from virtually any online store with our intuitive Chrome Extension.</p>
            </div>
            {/* Feature Card 2 */}
            <div className={`p-8 rounded-3xl transition duration-300 ${styles.glassCard}`}>
              <SquaresPlusIcon className="h-10 w-10 text-green-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Organize Like a Pro</h3>
              <p className="text-gray-600">Create multiple carts for different projects, gifts, or needs. Stay perfectly organized.</p>
            </div>
            {/* Feature Card 3 */}
            <div className={`p-8 rounded-3xl transition duration-300 ${styles.glassCard}`}>
              <LockClosedIcon className="h-10 w-10 text-red-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Private & Secure</h3>
              <p className="text-gray-600">Your shopping data is encrypted and handled with the utmost care. Shop with confidence.</p>
            </div>
             {/* Feature Card 4 */}
             <div className={`p-8 rounded-3xl transition duration-300 ${styles.glassCard}`}>
              <DevicePhoneMobileIcon className="h-10 w-10 text-yellow-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Access Anywhere</h3>
              <p className="text-gray-600">Sync your carts and access them from any device, anytime.</p>
            </div>
             {/* Feature Card 5 */}
             <div className={`p-8 rounded-3xl transition duration-300 ${styles.glassCard}`}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 text-indigo-500 mb-4"><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5" /></svg>
              <h3 className="text-xl font-semibold mb-3">Streamlined Checkout</h3>
              <p className="text-gray-600">Manage checkout processes more efficiently, saving you time and hassle. </p>
            </div>
             {/* Feature Card 6 - Call to action within features */}
             <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white p-8 rounded-3xl shadow-xl flex flex-col justify-center items-center text-center hover:shadow-2xl transition duration-300">
                <h3 className="text-xl font-semibold mb-3">Ready to Simplify?</h3>
                <p className="opacity-90 mb-5">Download the extension and start building your universal cart today.</p>
                <Link
                    to="/download"
                    className="bg-white/90 text-blue-700 font-semibold py-2 px-5 rounded-full shadow-md backdrop-blur-sm hover:bg-white transition duration-300 ease-in-out"
                >
                    Download Now
                </Link>
             </div>
          </div>
        </div>
      </section>

       {/* --- How It Works (Simplified & Visual) --- */}
      <section className="py-24">
        <div className="container mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">Get Started in Seconds</h2>
            <div className="flex flex-col md:flex-row justify-around items-center gap-12 md:gap-8">
                {/* Step 1 */}
                <div className={`text-center max-w-xs ${styles.stepItem}`}>
                    <div className="bg-white p-6 rounded-full shadow-lg inline-flex items-center justify-center mb-4 border border-gray-100">
                        <ArrowDownTrayIcon className="h-8 w-8 text-blue-500 mr-2" />
                        <span className="text-2xl font-bold text-blue-500">1</span>
                    </div>
                    <h3 className="font-semibold mb-1">Install Extension</h3>
                    <p className="text-sm text-gray-500">Add the ShopEase Chrome Extension to your browser.</p>
                </div>
                 {/* Step 2 */}
                 <div className={`text-center max-w-xs ${styles.stepItem}`}>
                    <div className="bg-white p-6 rounded-full shadow-lg inline-flex items-center justify-center mb-4 border border-gray-100">
                        <UserIcon className="h-8 w-8 text-green-500 mr-2" />
                         <span className="text-2xl font-bold text-green-500">2</span>
                    </div>
                    <h3 className="font-semibold mb-1">Sign In & Organize</h3>
                    <p className="text-sm text-gray-500">Log in and create your first universal shopping cart.</p>
                </div>
                 {/* Step 3 */}
                 <div className={`text-center max-w-xs ${styles.stepItem}`}>
                    <div className="bg-white p-6 rounded-full shadow-lg inline-flex items-center justify-center mb-4 border border-gray-100">
                        <ShoppingCartIcon className="h-8 w-8 text-purple-500 mr-2" />
                         <span className="text-2xl font-bold text-purple-500">3</span>
                    </div>
                    <h3 className="font-semibold mb-1">Shop & Add</h3>
                    <p className="text-sm text-gray-500">Browse your favorite stores and add items directly to ShopEase.</p>
                </div>
            </div>
        </div>
      </section>


      {/* --- Final Call to Action --- */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Ready to Revolutionize Your Shopping?
          </h2>
          <p className="text-lg md:text-xl opacity-90 mb-10 max-w-xl mx-auto">
            Join thousands of savvy shoppers simplifying their online experience. It's free!
          </p>
          <Link
            to="/register"
            className="bg-white text-blue-700 font-semibold py-4 px-10 rounded-full shadow-2xl hover:bg-gray-100 transition duration-300 ease-in-out transform hover:scale-105"
          >
            Sign Up Free Today
          </Link>
        </div>
      </section>

    </div>
  );
}

export default HomePage;

// Add this to your tailwind.config.js or a CSS file for the pulse animation:
/*
@layer utilities {
  @keyframes pulse-slow {
    0%, 100% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.05); }
  }
  .animate-pulse-slow {
    animation: pulse-slow 5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
}
*/
